import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { Shell } from "../components/Shell";
import { Ornament } from "../components/Ornament";
import { useAuth } from "../hooks/useAuth";
import { ApiTokensSection } from "../components/ApiTokensSection";
import type { InviteResponse } from "../lib/types";

export function InvitePage() {
  const { me } = useAuth();
  const [email, setEmail] = useState("");
  const [result, setResult] = useState<InviteResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const mut = useMutation({
    mutationFn: (e: string) =>
      api.post<InviteResponse>("/api/auth/invite", { email: e }),
    onSuccess: (data) => {
      setResult(data);
      setError(null);
    },
    onError: (e: unknown) => {
      if (e instanceof ApiError) setError(e.detail);
      else setError("Failed to write invitation.");
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    setCopied(false);
    mut.mutate(email);
  };

  const copy = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(result.accept_url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2400);
  };

  return (
    <Shell>
      <header className="mb-20 grid grid-cols-12 gap-8 border-b border-ink/80 pb-14">
        <div
          className="col-span-12 md:col-span-3 animate-fade-up"
          style={{ animationDelay: "60ms" }}
        >
          <div className="label mb-3 text-ink-walnut">Section</div>
          <div className="label text-sienna">N° 02</div>
        </div>
        <div
          className="col-span-12 md:col-span-9 animate-fade-up"
          style={{ animationDelay: "140ms" }}
        >
          <h1 className="display-1">
            The <span className="display-italic">Household</span>
          </h1>
          <p className="mt-8 max-w-2xl font-body text-xl italic leading-snug text-ink-walnut">
            <span className="not-italic font-mono text-[11px] uppercase tracking-widest text-sienna mr-2">
              {me?.household.name}
            </span>
            — invite someone else to share the shelves and the shopping list.
          </p>
        </div>
      </header>

      <div className="grid grid-cols-12 gap-x-8 gap-y-16">
        <div
          className="col-span-12 md:col-span-7 md:col-start-3 animate-fade-up"
          style={{ animationDelay: "220ms" }}
        >
          <form onSubmit={onSubmit} className="space-y-10">
            <div>
              <label className="label mb-2 block text-ink-walnut">
                Their email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="partner@example.com"
                className="field-input"
              />
            </div>

            {error && (
              <div className="border-l-2 border-sienna pl-4 font-body italic text-sienna animate-fade-in">
                {error}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-8">
              <button
                type="submit"
                className="btn-ink"
                disabled={mut.isPending}
              >
                {mut.isPending ? "Writing…" : "Write an invitation"}
              </button>
              <span className="label text-ink-muted">
                You'll get a link to send by hand.
              </span>
            </div>
          </form>

          {result && (
            <div className="mt-20 animate-fade-up">
              <div className="mb-6 flex items-center gap-4">
                <Ornament className="h-4 w-16 text-sienna" />
                <div className="label text-ink-walnut">The invitation</div>
                <span className="hairline-ink flex-1" />
              </div>
              <p className="mb-6 font-body text-lg italic text-ink-walnut">
                Send this link by post, by message, by hand. Valid for{" "}
                {result.expires_in_days} days.
              </p>
              <div className="border-l-2 border-sienna bg-parchment-deep/70 p-6">
                <code className="block break-all font-mono text-sm leading-relaxed text-ink">
                  {result.accept_url}
                </code>
                <div className="mt-5 flex items-center justify-between gap-4">
                  <span className="label text-ink-muted">
                    {copied ? "Copied to clipboard" : "Single-use"}
                  </span>
                  <button
                    type="button"
                    onClick={copy}
                    className="btn-ghost py-2.5"
                  >
                    {copied ? "Copied" : "Copy link"}
                  </button>
                </div>
              </div>
            </div>
          )}

          <ApiTokensSection />
        </div>
      </div>
    </Shell>
  );
}
